import type { ChangeEvent, ReactNode } from 'react'

/* =======================================================================
 * FORMA COMUNA A PANOURILOR CU FORMULAR
 * =======================================================================
 *
 * Bucatile astea au fost scrise pe 10 septembrie 2026 inauntrul lui
 * `settings/users/AccountWorkspace.tsx`, cand cele cinci panouri ale fisei
 * unui cont au fost aduse pe aceeasi grila. Acum le foloseste si
 * `settings/my_account/`, deci stau intr-un singur loc: doua copii ale unei
 * asezari sunt EXACT felul in care panourile s-au departat unul de altul prima
 * data, si aia e toata pricina pentru care se face munca asta.
 *
 * FORMA nu e inventata aici: e cea care era deja cea mai raspandita in
 * machetele portalului - opt din cele zece randuri ale lui `editForm.php`,
 * randul de IMEI din Privacy, randul din Video Conference:
 *
 *   - fiecare camp are aceeasi latime si aceeasi inaltime, pe aceleasi doua
 *     margini de coloana, cu ETICHETA DEASUPRA campului;
 *   - fiecare bifa e patratelul mic obisnuit, cu eticheta LANGA ea;
 *   - o singura abatere, cu buna stiinta: o pereche de raspunsuri de un cuvant
 *     imparte coloana din dreapta in doua jumatati (`span={3}`), care se termina
 *     exact acolo unde se termina si celelalte campuri.
 *
 * Clasele sunt ale portalului (`form-group`, `row`, `col-md-*`, `form-control`,
 * `control-label`, `help-block`). Nici o foaie de stil noua, nici o dependinta
 * noua - portalul asta trece printr-un audit bancar.
 *
 * CE NU INTRA PE GRILA, si de ce:
 *   - o CITIRE (`details.php`, chipul din `profile_sidebar.php`), unde eticheta
 *     langa valoare spune mai mult decat deasupra ei, si unde randurile puse pe
 *     cate doua etaje ar face un panou de doua ori mai inalt fara sa spuna nimic
 *     in plus;
 *   - o casuta de FISIER, care isi taie singura butonul nativ daca primeste
 *     `form-control` (inaltime fixa) - sta pe coloana, dar fara clasa;
 *   - un TABEL, care isi are latimile lui;
 *   - un camp cu ADDON (`input-group`, campul procentual din
 *     `DeviceEndpointPerformanceSettings`), care sta pe coloana intreaga, dar
 *     isi pastreaza asezarea de tabel a Bootstrap-ului - vezi `v4.css`;
 *   - `PermissionsGrid`, care are grila lui, dintr-un motiv scris acolo.
 */

/**
 * Randul de doua coloane. Toate randurile de formular trec pe aici.
 *
 * `od-form-row` E MOTIVUL PENTRU CARE GRILA ASTA SE VEDE
 * -----------------------------------------------------------------------------
 *
 * Panourile se deschid inauntrul unui rand desfasurat de tabel, iar invelisul
 * listei poarta `form-inline` (`DataTable`: `dataTables_wrapper form-inline
 * dt-bootstrap`, exact clasa pe care o scrie DataTables si in portal). De la
 * 768px in sus, Bootstrap are acolo doua reguli care desfac orice grila
 * dinauntru (`app.css`, 2987 si 2992):
 *
 *     .form-inline .form-group  { display: inline-block; }
 *     .form-inline .form-control { display: inline-block; width: auto; }
 *
 * Amandoua apuca randurile DE AICI, fiindca sunt `.form-group` cu
 * `.form-control` in ele inauntrul acelui invelis. Randul inline-block se
 * stramteaza la continutul lui, deci `col-md-6` nu mai e jumatate de panou ci
 * jumatate din cat a iesit; iar campul nu mai umple coloana, ci ramane cat il
 * face browserul.
 *
 * Masurat in Chromium (1600px, foile adevarate servite pe HTTP, panoul in randul
 * desfasurat): coloana lui „Collaborator" 76px si a lui „IT Manager" 187px in
 * loc de 672px, campul „First name" 183px intr-o coloana de 672px. De-aia
 * ADUCEREA PE GRILA nu s-a vazut deloc pe ecran, desi markupul se schimbase:
 * regulile ei erau scoase din joc inainte sa apuce ceva. „Pe expand taburile
 * pe care ti le-am zis si nu e nimic facut pe ele" - asta era.
 *
 * Clasa se scoate din joc inapoi in `v4.css`, cu doua reguli care au un `body`
 * in fata ca sa treaca de cele doua de mai sus. Pe My Account, care nu sta
 * intr-un tabel, nu schimba nimic: acolo `.form-group` e block si
 * `.form-control` e 100% oricum.
 */
export function FormRow({ children }: { children: ReactNode }) {
  return <div className="form-group row od-form-row">{children}</div>
}

/**
 * Cate coloane tine un camp. Trei valori, si nu mai multe - o grila cu
 * douasprezece latimi la indemana nu mai e o grila, e o sugestie.
 */
export type FieldSpan = 3 | 6 | 12

function colClass(span: FieldSpan): string {
  if (span === 3) return 'col-md-3'
  if (span === 12) return 'col-md-12'
  return 'col-md-6'
}

/**
 * O jumatate de rand: eticheta deasupra, campul dedesubt, lamurirea sub camp.
 * `span` e 6 (jumatate) peste tot, afara de perechile mici de un cuvant.
 *
 * `span={12}`, SI CAND SE FOLOSESTE
 * -----------------------------------------------------------------------------
 *
 * Adaugat pe 10 septembrie 2026, cerut de doua panouri din Proactivitee si de
 * dialogul de mesaje din Opteemum. Nu e o portita: e pentru o cutie al carei
 * CONTINUT e lat - o comanda scrisa pe mai multe randuri, un mesaj, un petec de
 * jurnal lipit inauntru. Acolo jumatatea de rand chiar taie ce trebuie citit, si
 * omul deruleaza pe orizontala intr-un `textarea` ca sa-si vada propria comanda.
 *
 * NU se pune pe un camp de o linie. Un nume, un email, o data, un select - alea
 * raman pe jumatate CHIAR CAND SUNT SINGURE PE RAND, cu loc gol la dreapta:
 * exact asta s-a facut in Settings, unde `changePassword.php` si primele trei
 * campuri din `privacySettings.php` au fost aduse de la 942px la 456px tocmai ca
 * marginea din dreapta sa fie aceeasi pe toate ecranele care se deschid din
 * aceeasi banda de file. Un camp de o linie intins la 942px nu castiga nimic si
 * strica singurul lucru pe care grila il promite.
 *
 * Si sta SINGUR pe randul lui: douasprezece coloane inseamna randul intreg.
 */
export function HalfField({
  label,
  children,
  required = false,
  help,
  span = 6,
  htmlFor,
}: {
  label: string
  children: ReactNode
  required?: boolean
  help?: ReactNode
  span?: FieldSpan
  htmlFor?: string
}) {
  return (
    <div className={colClass(span)}>
      <label className="control-label" htmlFor={htmlFor}>
        {label} {required ? <span className="required">*</span> : null}
      </label>
      {children}
      {help ? <span className="help-block">{help}</span> : null}
    </div>
  )
}

/**
 * O jumatate de rand cu o bifa: bifa intai, eticheta la dreapta ei.
 * Forma asta o aveau deja Collaborator, cele doua din Privacy si cele doua din
 * Video Conference - patru din sase - deci la ea au venit si celelalte.
 *
 * CE E `className` SI DE CE RAMANE
 * -----------------------------------------------------------------------------
 *
 * Sunt CARLIGELE PLUGINURILOR din portal, si nu sunt stiluri: niciun
 * `public/css/*.css` nu are vreo regula pe ele. In portal, javascriptul paginii
 * le cauta si inlocuieste bifa cu markup desenat - `editForm-1.js:57`
 * porneste `$('.icheck-user').iCheck({checkboxClass: 'icheckbox_minimal-red'})`,
 * iar `.collaborator` si `#has_quick_chat_support` trec prin Switchery. De-acolo
 * si plangerea de la care a pornit munca asta: trei bifari intr-un formular, in
 * trei forme - un patrat rosu de sprite si doua comutatoare.
 *
 * IN v4 NU RULEAZA NICIUNUL. Bifa e desenata de `od_console.css` (sectiunea
 * 5): casuta adevarata a browserului, 15x15, in cerneala zonei
 * (`accent-color: var(--od-ink)`). Foaia scrie chiar acolo de ce s-a ales asa
 * si nu pluginul - „bifa ramane bifa: o citeste si cititorul de ecran, si
 * tastatura o gaseste singura" - iar decizia aia e mai noua decat pluginul si
 * il inlocuieste. Deci nu se porteaza iCheck aici: forma ceruta e cea pe care
 * portalul insusi a ales-o pentru zona asta.
 *
 * Numele raman scrise fiindca spun de unde vine campul si ce facea el in
 * portal, si fiindca sunt tinta pe care o cauta cine compara cele doua ecrane.
 * Nu sterge unul singur: ori toate sase, ori niciunul.
 *
 * `od-check` E MOTIVUL PENTRU CARE BIFA ARATA LA FEL SI IN AFARA SETARILOR
 * -----------------------------------------------------------------------------
 *
 * Bucata asta s-a nascut in Settings, unde `od_console.css` era aprinsa - clasa
 * `od-console` de pe `<body>` o da doar zonelor din `od_console_zones()`, azi
 * `settings` si `feedback`. Pe 10 septembrie 2026 aceleasi bucati au fost duse
 * in panourile fisei unui aparat (Inventoree), unde clasa NU e pusa: acolo
 * sectiunea 5 a foii nu apuca nimic, si bifa ramanea cea a browserului -
 * masurat 13x13px, cu albastrul lui, langa 15x15 in cerneala paginii pe acelasi
 * formular din Settings.
 *
 * Nu se aprinde `od-console` pe Inventoree ca sa se repare o bifa: clasa aia
 * schimba desenul unui modul intreg (card, buton, tabel, titlu de pagina) si e
 * o hotarare de pus in fata omului, nu un efect lateral. Se muta insa cele
 * cinci randuri care privesc CHIAR BUCATA ASTA in `v4.css`, sub `od-check`,
 * unde nu depind de zona. In Settings nu schimba nimic: valorile sunt aceleasi
 * pe care le scria deja foaia.
 *
 * `name` si `value` sunt pentru formularele citite cu `FormData` (fisa
 * aparatului trimite exact ce trimitea `ajaxForm` in portal). Fara ele, bifa
 * nu apare deloc in ce se trimite.
 */
export interface CheckProps {
  id: string
  label: string
  className?: string
  /** Numele si valoarea trimise, acolo unde formularul se citeste cu `FormData`. */
  name?: string
  value?: string
  /**
   * CONTROLATA: starea sta in React, si se dau amandoua.
   * NECONTROLATA: se da in schimb `defaultChecked`, si starea sta in DOM.
   * Una sau alta, niciodata amandoua - React se plange, pe drept, daca o bifa
   * isi schimba felul intre doua desenari.
   */
  checked?: boolean
  onChange?: (value: boolean) => void
  /** Bifa necontrolata: pentru formularele care isi citesc valorile din DOM. */
  defaultChecked?: boolean
}

/**
 * BIFA SINGURA, fara coloana in jurul ei.
 *
 * Scoasa din `CheckField` pe 10 septembrie 2026, cand s-a vazut ca panourile
 * din Opteemum au grupuri de zeci de bife - o lista de comenzi - iar
 * `col-md-6` le facea de doua ori mai inalte decat trebuie. Agentul de acolo
 * incepuse sa rescrie markupul dinauntru de mana, si aia e chiar felul in care
 * cele sapte panouri ale unui cont s-au departat unul de altul.
 *
 * Deci bifa se ia de aici SI CAND randul din jur nu e un rand de formular:
 * casuta, marimea, cerneala si spatiul pana la cuvant raman ale ei, iar
 * invelisul e al celui care o aseaza.
 *
 * DAR: zeci de bife nu mai sunt un formular, sunt un ALEGATOR. Cand grupul
 * creste - cautare, "bifeaza tot", derulare proprie - locul lui nu e un
 * `FormRow` tot mai lung, ci o bucata a lui, cum e `PermissionsGrid`. Bucata
 * asta ii da bifa potrivita; nu-i da si o grila care sa tina zeci de ele.
 */
export function CheckBox({ id, label, className, name, value, checked, onChange, defaultChecked }: CheckProps) {
  const state =
    defaultChecked === undefined
      ? { checked: checked ?? false, onChange: (e: ChangeEvent<HTMLInputElement>) => onChange?.(e.target.checked) }
      : { defaultChecked, onChange: (e: ChangeEvent<HTMLInputElement>) => onChange?.(e.target.checked) }
  return (
    <span className="od-check">
      <input type="checkbox" id={id} className={className} name={name} value={value} {...state} />
      <label className="control-label" style={{ marginLeft: 10 }} htmlFor={id}>
        {label}
      </label>
    </span>
  )
}

/**
 * O jumatate de rand cu o bifa. `span` e 6 peste tot; 3 cand o pereche de bife
 * scurte incape pe un sfert, 12 cand cuvantul de langa ea e o propozitie.
 */
export function CheckField({ span = 6, ...check }: CheckProps & { span?: FieldSpan }) {
  return (
    <div className={colClass(span)}>
      <CheckBox {...check} />
    </div>
  )
}

/**
 * Numele unui GRUP de bife, pe randul lui, deasupra lor.
 *
 * Grila n-are titluri de sectiune, si nici nu-i trebuie cat timp fiecare camp
 * isi poarta numele deasupra. Doua locuri din fisa unui aparat aveau insa un
 * nume care ACOPEREA mai multe bife, si fara el bifele raman fara subiect:
 * "Agent install settings" peste Auto update / Beta version (Settings) si
 * "Network Connection" peste Enable network connection (Endpoint performance).
 * In machetele vechi numele ala statea in coloana de etichete a formularului
 * orizontal, care aici nu mai exista.
 *
 * Deci se scrie o data, aici, si nu de doua ori in doua panouri: doua copii ale
 * unei asezari sunt chiar felul in care panourile s-au departat unul de altul.
 * NU e o eticheta de camp - nu are `htmlFor`, fiindca nu arata spre un singur
 * camp, ci spre randul de sub el.
 */
export function FormCaption({ children }: { children: ReactNode }) {
  return (
    <FormRow>
      <div className="col-md-12">
        <label className="control-label">{children}</label>
      </div>
    </FormRow>
  )
}

/** Randul butoanelor. `margiv-top-10` din machetele vechi nu exista in nicio foaie. */
export function FormActions({ children }: { children: ReactNode }) {
  return <div style={{ marginTop: 10 }}>{children}</div>
}
